import React, { useState } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db, functions } from '../firebase'; // Ensure functions is exported from your firebase config
import { httpsCallable } from 'firebase/functions';
import toast from 'react-hot-toast';
import confetti from 'canvas-confetti';
import Rating from './rating';

const GOLD = '#B8860B';
const NAVY = '#001F3F';

export default function BookingCompletionUI({ booking }) {
  const [loading, setLoading] = useState(false);
  const [showRating, setShowRating] = useState(false);

  const toastStyle = {
    style: {
      background: NAVY,
      color: GOLD,
      border: `1px solid ${GOLD}`,
      fontSize: '0.85rem',
      borderRadius: '12px',
      padding: '12px 20px',
      fontWeight: '600',
    }
  };

  const fireConfetti = () => {
    const end = Date.now() + 2 * 1000;
    const colors = ['#B8860B', '#FFD700', '#ffffff'];

    (function frame() {
      confetti({ particleCount: 3, angle: 60, spread: 55, origin: { x: 0 }, colors: colors });
      confetti({ particleCount: 3, angle: 120, spread: 55, origin: { x: 1 }, colors: colors });
      if (Date.now() < end) requestAnimationFrame(frame);
    }());
  };

  const handleComplete = async () => {
    if (!window.confirm("Confirm completion? Funds will be released to the stylist.")) return;

    const loadingToast = toast.loading("Releasing funds from Vault...", toastStyle);
    setLoading(true);

    try {
      // 1. Call the Cloud Function to trigger the Monnify 90/10 Split
      const releaseFunds = httpsCallable(functions, 'releaseEscrowFunds');
      await releaseFunds({ bookingId: booking.id });

      // 2. Update Firestore document status
      await updateDoc(doc(db, 'bookings', booking.id), {
        status: 'completed',
        completedAt: serverTimestamp(),
        escrowStatus: 'released',
        lastUpdated: serverTimestamp()
      });

      toast.success("Stylist Paid! 90% sent to their wallet.", { id: loadingToast, ...toastStyle });
      
      fireConfetti();
      setTimeout(() => setShowRating(true), 2500);
    } catch (err) {
      console.error("Completion Error:", err);
      toast.error("Error releasing funds. Please try again.", { id: loadingToast, ...toastStyle });
    } finally {
      setLoading(false);
    }
  };

  const handleDispute = async () => {
    const reason = window.prompt("Reason for dispute:");
    if (!reason) return;
    
    setLoading(true);
    try {
        await updateDoc(doc(db, 'bookings', booking.id), {
            isDisputed: true, 
            disputeReason: reason, 
            escrowStatus: 'frozen', 
            status: 'disputed',
            lastUpdated: serverTimestamp()
        });
        toast.success("Dispute filed. Funds frozen.", toastStyle);
    } catch (err) {
        toast.error("Error filing dispute.", toastStyle);
    } finally {
        setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("Cancel booking? Refund goes to your wallet.")) return;
    setLoading(true);
    try {
        await updateDoc(doc(db, 'bookings', booking.id), {
            status: 'cancelled', 
            cancelledAt: serverTimestamp(), 
            escrowStatus: 'refund_pending',
            lastUpdated: serverTimestamp()
        });
        toast.success("Booking cancelled. Refund processing.", toastStyle);
    } catch (err) {
        toast.error("Error during cancellation.", toastStyle);
    } finally {
        setLoading(false);
    }
  };

  return (
    <>
      <div style={containerStyle}>
        <div style={headerRowStyle}>
          <h4 style={titleStyle}>SESSION CONTROL</h4>
          {(booking.status === 'paid' || booking.status === 'confirmed') && (
             <button onClick={handleCancel} disabled={loading} style={cancelBtn}>Cancel & Refund</button>
          )}
        </div>

        <div style={buttonGroupStyle}>
          <button 
            onClick={handleComplete} 
            disabled={loading || booking.status === 'completed'}
            style={{
              ...completeBtn,
              opacity: (loading || booking.status === 'completed') ? 0.4 : 1,
              cursor: (loading || booking.status === 'completed') ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Finalizing...' : booking.status === 'completed' ? 'Completed' : 'Mark as Completed'}
          </button>
          
          <button 
            onClick={handleDispute} 
            disabled={loading || booking.isDisputed || booking.status === 'completed'}
            style={{
              ...disputeBtn,
              opacity: (booking.isDisputed || booking.status === 'completed') ? 0.2 : 1,
              cursor: (booking.isDisputed || booking.status === 'completed') ? 'not-allowed' : 'pointer'
            }}
          >
            Dispute
          </button>
        </div>

        {booking.isDisputed && (
          <div style={disputeBadgeStyle}>⚠️ This session is currently under dispute.</div>
        )}
      </div>

      {showRating && (
        <Rating 
          bookingId={booking.id} 
          stylistId={booking.barberId} 
          onClose={() => setShowRating(false)} 
        />
      )}
    </>
  );
}

const containerStyle = { background: 'rgba(255,255,255,0.02)', padding: '24px', borderRadius: '20px', marginTop: '20px', border: '1px solid rgba(184,134,11,0.15)', backdropFilter: 'blur(12px)' };
const headerRowStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' };
const titleStyle = { color: GOLD, margin: 0, fontSize: '0.75rem', letterSpacing: '2px', fontWeight: '900' };
const buttonGroupStyle = { display: 'flex', gap: '12px' };
const completeBtn = { flex: 2, background: GOLD, color: 'black', border: 'none', padding: '15px', borderRadius: '12px', fontWeight: 'bold', fontSize: '0.8rem' };
const disputeBtn = { flex: 1, background: 'transparent', color: '#ff4d4d', border: '1px solid rgba(255, 77, 77, 0.4)', padding: '15px', borderRadius: '12px', fontWeight: 'bold', fontSize: '0.8rem' };
const cancelBtn = { background: 'none', border: 'none', color: '#888', textDecoration: 'underline', fontSize: '0.7rem', cursor: 'pointer' };
const disputeBadgeStyle = { background: 'rgba(255, 77, 77, 0.08)', color: '#ff4d4d', padding: '12px', borderRadius: '10px', fontSize: '0.75rem', marginTop: '15px', textAlign: 'center', border: '1px solid rgba(255, 77, 77, 0.2)' };
